
import { takeUntil } from 'rxjs/operators';
import { Component, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { DistributorFinderApi } from '../../../../../api/dealerLocator/DealerLocatorApi';
import { DealerLocatorService } from '../../dealerLocator/DistributorFinder.service';
import { IDistributor } from '../../../../../model/dealerLocator/IDistributor';
import { Subject } from 'rxjs';
import { AppConstants } from '../../../../../constants/constants';
import { IAlert } from 'tridion.web.ui/src/app/angular/layout/alert/IAlert';
import { AlertType } from 'tridion.web.ui/src/app/angular/layout/alert/AlertType';
import { AlertService } from 'tridion.web.ui/src/app/angular/layout/alert/Alert.service';
import { MapsAPILoader } from '@agm/core';

@Component({
    selector: 'admin-dealer-modal-edit',
    templateUrl: 'DistributorModalEdit.component.html'
})
export class DealerLocatorModalEditComponent implements OnInit, OnDestroy {
    readonly geolocationResult: string;
    @Input()
    page: number;
    @Input()
    limit: number;
    response: string;
    closeResult: string;
    showModal = false;
    spinner: boolean;
    searchForm: FormGroup;
    distributor: IDistributor;
    distributorNewValues: IDistributor;
    distributorForm: FormGroup;
    private unsubscribe: Subject<void> = new Subject();

    constructor(
        private distributorFinderApi: DistributorFinderApi,
        private dealerLocatorService: DealerLocatorService,
        private fb: FormBuilder,
        private alertService: AlertService,
        private mapsAPILoader: MapsAPILoader,
    ) { }

    public getCountryAndSegment(): { country: string, segment: string } {
        const countryObj = JSON.parse(window.sessionStorage.getItem('jobstSelectedCountry') || window.localStorage.getItem('jobstSelectedCountry'));
        const segmentObj = JSON.parse(window.sessionStorage.getItem('jobstSelectedSegment') || window.localStorage.getItem('jobstSelectedSegment'));

        const country = countryObj && countryObj.objectId ? countryObj.objectId.toString() : '';
        const segment = segmentObj && segmentObj.objectId ? segmentObj.objectId.toString() : '';

        return { country, segment };
    }

    ngOnInit() {
        this.dealerLocatorService.distributor.pipe(
            takeUntil(this.unsubscribe))
            .subscribe((distributor: IDistributor) => {
                this.distributor = distributor;
            });

        this.dealerLocatorService.distributorNewValues.pipe(
            takeUntil(this.unsubscribe))
            .subscribe((distributor: any) => {
                this.distributorNewValues = distributor;
                if (distributor) {
                    if (!this.distributorForm) {
                        this.buildForm();
                    } else {
                        for (let item in this.distributorForm.value) {
                            if (
                                distributor[item] !== this.distributorForm.value[item] &&
                                distributor[item]
                            ) {
                                this.distributorForm.controls[item].setValue(
                                    distributor[item]
                                );
                            }
                        }
                        this.distributorForm.controls.geoCoordinates.setValue(
                            distributor.geoCoordinates
                        );
                    }
                    this.toggleEditModal(true);
                }
            });
    }

    buildForm() {
        let regex = new RegExp(AppConstants.webSiteUrlPattern);
        let phonePattern = new RegExp('^[0-9-+/() ]*$');
        let prioPattern = new RegExp(/^(?!.*-).*$/);

        this.distributorForm = this.fb.group({
            name: new FormControl(this.distributorNewValues.name, Validators.required),
            street: new FormControl(
                this.distributorNewValues.street,
                Validators.required
            ),
            location: new FormControl(this.distributorNewValues.location, Validators.required),
            zip: new FormControl(this.distributorNewValues.zip, Validators.required),
            geoCoordinates: new FormControl(
                this.distributorNewValues.geoCoordinates,
                Validators.required
            ),
            phone: new FormControl(
                this.distributorNewValues.phone,
                [Validators.required, Validators.pattern(phonePattern)]
            ),
            url: new FormControl(
                this.distributorNewValues.url,
                Validators.pattern(regex)
            ),
            prio: new FormControl(this.distributorNewValues.prio, Validators.pattern(prioPattern)),
            filter1: new FormControl(this.distributorNewValues.filter1),
            filter2: new FormControl(this.distributorNewValues.filter2),
            filter3: new FormControl(this.distributorNewValues.filter3),
            filter4: new FormControl(this.distributorNewValues.filter4),
            filter5: new FormControl(this.distributorNewValues.filter5)
        });
    }

    getAll() {
        this.spinner = true;

        const { country, segment } = this.getCountryAndSegment();

        this.distributorFinderApi
            .getDistributors(this.limit, this.page === 0 ? 1 : this.page, this.searchForm, country, segment)
            .pipe(takeUntil(this.unsubscribe))
            .subscribe(response => {
                this.dealerLocatorService.totalCount = response.totalCount;
                this.dealerLocatorService.distributors.next(response.distributors);
                this.spinner = false;
            });
    }

    saveDistributor() {
        if (this.distributorForm.invalid) {
            return;
        }

        Object.assign(this.distributorNewValues, this.distributorForm.value);
        const { country, segment } = this.getCountryAndSegment();

        this.distributorFinderApi
            .update(this.distributorNewValues, country, segment)
            .pipe(takeUntil(this.unsubscribe))
            .subscribe({
                next: (res: IDistributor) => {
                    this.getAll();

                    this.dealerLocatorService.distributorNewValues.next(undefined);
                    this.dealerLocatorService.distributor.next(null);

                    this.response = 'Distributor changed!';
                    setTimeout(() => {
                        this.toggleEditModal(false);
                    }, 2500);
                },
                error: (error) => {
                    if (error._body) {
                        let alert: IAlert = {
                            message: '',
                            type: AlertType.error,
                            close: true,
                        };
                        alert.message = error._body;

                        this.alertService.setAlert(alert);
                    }
                    this.response = error.statusText;
                },
            });
    }

    addDistributor() {
        if (this.distributorForm.invalid) {
            return;
        }

        Object.assign(this.distributorNewValues, this.distributorForm.value);
        const { country, segment } = this.getCountryAndSegment();

        this.distributorFinderApi
            .add(this.distributorNewValues, country, segment)
            .pipe(takeUntil(this.unsubscribe))
            .subscribe({
                next: () => {
                    this.getAll();

                    this.dealerLocatorService.distributorNewValues.next(null);
                    this.response = 'Distributor added successfully!';

                    setTimeout(() => {
                        this.toggleEditModal(false);
                    }, 2500);
                },
                error: (err) => {
                    this.response = 'Error adding distributor. Please try again.';
                }
            });
    }

    resetForm() {
        if (this.distributor === null) {
            this.distributor = undefined;
        }
        this.distributorForm.reset(this.distributor);
    }

    toggleEditModal(show: boolean) {
        if (show) {
            document.getElementsByTagName('body')[0].className = 'modal-open';
        } else {
            this.response = '';
            this.dealerLocatorService.distributor.next(undefined);
            this.dealerLocatorService.distributorNewValues.next(null);

            this.distributorForm.reset();
            this.distributorForm.patchValue({ Filter1: false })
            this.distributorForm.patchValue({ Filter2: false })
            this.distributorForm.patchValue({ Filter3: false })
            this.distributorForm.patchValue({ Filter4: false })
            this.distributorForm.patchValue({ Filter5: false })

            document.getElementsByTagName('body')[0].classList.remove('modal-open');
        }

        this.showModal = show;
    }

    @HostListener('click', ['$event.target.className'])
    onClick(elementClass: string) {
        if (elementClass === 'modal') {
            this.toggleEditModal(false);
        }
    }
    ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }
}
